import { useCallback, useEffect, useState } from "react";
import { Eye, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rupiah, tanggal } from "@/lib/format";
import { DONATION_TYPES, STATUS_LABEL, STATUS_TONE } from "@/lib/donation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PeriodFilter, periodParams } from "./PeriodFilter";
import { DonationDetail } from "./DonationDetail";

export const DonationList = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [month, setMonth] = useState("all");
  const [year, setYear] = useState("all");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams(periodParams(month, year));
      if (status !== "all") p.set("status", status);
      const { data } = await api.get(`/donations?${p.toString()}`);
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [status, month, year]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatusOf = async (d, newStatus) => {
    setBusy(d.id);
    try {
      await api.patch(`/donations/${d.id}/status`, { status: newStatus });
      toast.success(newStatus === "dikonfirmasi" ? "Donasi dikonfirmasi & dicatat ke kas" : "Donasi ditolak");
      setDetail(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-3xl bg-white border border-emerald-900/10 overflow-hidden" data-testid="donation-list">
      <div className="p-4 border-b border-emerald-900/10 flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-52 rounded-full" data-testid="donation-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`donation-status-option-${k}`}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <PeriodFilter month={month} year={year} setMonth={setMonth} setYear={setYear} prefix="donation-list" />
        <span className="ml-auto text-sm text-slate-500" data-testid="donation-count">{items.length} donasi</span>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="donation-table">
          <TableHeader>
            <TableRow>
              <TableHead>Tanggal</TableHead><TableHead>Donatur</TableHead><TableHead>Jenis</TableHead><TableHead>Metode</TableHead>
              <TableHead className="text-right">Nominal</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} />Memuat...</TableCell></TableRow>}
            {!loading && items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400" data-testid="donation-empty">Belum ada donasi.</TableCell></TableRow>}
            {items.map((d) => (
              <TableRow key={d.id} data-testid={`donation-row-${d.id}`}>
                <TableCell>{tanggal(d.date)}</TableCell>
                <TableCell className="font-medium">{d.real_name}{d.anonymous && <span className="text-xs text-slate-400 ml-1">(Anonim)</span>}</TableCell>
                <TableCell>{DONATION_TYPES[d.type]}</TableCell>
                <TableCell className="uppercase text-xs text-slate-500">{d.method === "transfer" ? "Transfer" : "QRIS"}</TableCell>
                <TableCell className="text-right font-semibold text-emerald-800">{rupiah(d.amount)}</TableCell>
                <TableCell><Badge className={`${STATUS_TONE[d.status]} hover:${STATUS_TONE[d.status]}`} data-testid={`donation-status-${d.id}`}>{STATUS_LABEL[d.status]}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => setDetail(d)} data-testid={`donation-view-${d.id}`}><Eye size={15} /></Button>
                  {d.status !== "dikonfirmasi" && (
                    <Button variant="ghost" size="icon" className="text-emerald-700" disabled={busy === d.id} onClick={() => setStatusOf(d, "dikonfirmasi")} data-testid={`donation-confirm-${d.id}`}><Check size={16} /></Button>
                  )}
                  {d.status !== "ditolak" && (
                    <Button variant="ghost" size="icon" className="text-red-600" disabled={busy === d.id} onClick={() => setStatusOf(d, "ditolak")} data-testid={`donation-reject-${d.id}`}><X size={16} /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <DonationDetail donation={detail} onClose={() => setDetail(null)} onConfirm={(d) => setStatusOf(d, "dikonfirmasi")} onReject={(d) => setStatusOf(d, "ditolak")} busy={!!busy} />
    </div>
  );
};
